import React, { useState } from 'react';
import {
  Send,
  Upload,
  Image as ImageIcon,
  FileText,
  Video,
  Smile,
  X,
  Users,
  User,
  Search,
  Plus
} from 'lucide-react';
import { useWhatsappSendMutation } from '@/client/api/whatsapp/send.mutation';
import { useBroadcastWithMediaMutation } from '@/client/api/whatsapp/broadcast.mutation';
import { useSendWithMediaMutation } from '@/client/api/whatsapp/send-with-media.mutation';
import { useListContactsQuery } from '@/client/api/whatsapp/list-contacts.query';
import { useListNumbersQuery } from '@/client/api/whatsapp/list-numbers.query';
import { isLinkedNumber, useSenderLabel } from './NumberBadge';
import { toast, Toaster } from 'sonner';

interface Contact {
  id: string;
  name: string;
  phone: string;
  avatar?: string;
}

const SendMessagePage: React.FC = () => {
  const { mutateAsync: sendSingleMessage, isPending: sendIsPending } = useWhatsappSendMutation();
  const { mutateAsync: sendWithAttachments, isPending: sendWithAttachmentsIsPending } = useSendWithMediaMutation();
  const { mutateAsync: broadcastMutation, isPending: broadcastIsPending } = useBroadcastWithMediaMutation();
  const { data: contactsData } = useListContactsQuery();
  const { data: numbersData } = useListNumbersQuery();
  const senderLabel = useSenderLabel();

  const [messageType, setMessageType] = useState<'single' | 'multiple'>('single');
  const [selectedContacts, setSelectedContacts] = useState<Contact[]>([]);
  const [messageText, setMessageText] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [attachments, setAttachments] = useState<File[]>([]);
  const [showContactList, setShowContactList] = useState(false);
  // '' = the server's default number.
  const [fromNumber, setFromNumber] = useState('');

  const contacts: Contact[] = (contactsData?.data?.contacts || []).map(c => ({
    id: c.contact_id,
    name: c.name,
    phone: c.phone,
  }));

  const filteredContacts = contacts.filter(contact =>
    contact.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    contact.phone.includes(searchQuery)
  );

  const handleContactSelect = (contact: Contact) => {
    if (messageType === 'multiple') {
      if (!selectedContacts.find(c => c.id === contact.id)) {
        setSelectedContacts([...selectedContacts, contact]);
      }
    } else {
      setSelectedContacts([contact]);
    }
    setShowContactList(false);
    setSearchQuery('');
  };

  const handleMessageTypeChange = (type: 'single' | 'multiple') => {
    if(type === 'single') {
      setSelectedContacts([selectedContacts[0]]);
    } 
    setMessageType(type);
  };

  const handleRemoveContact = (contactId: string) => {
    setSelectedContacts(selectedContacts.filter(c => c.id !== contactId));
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setAttachments([...attachments, ...Array.from(e.target.files)]);
    }
  };

  const handleRemoveAttachment = (index: number) => {
    setAttachments(attachments.filter((_, i) => i !== index));
  };

  const handleSendMessage = async () => {
    try {
      if (messageType === 'single') {
        if (attachments.length > 0) {
          const formData = new FormData();
          formData.append('to', selectedContacts[0].phone);
          formData.append('message', messageText);
          attachments.forEach((file) => formData.append('files', file));
          if (fromNumber) formData.append('from_number', fromNumber);
          await sendWithAttachments(formData);
        } else {
          await sendSingleMessage({
            to: selectedContacts[0].phone,
            type: 'text',
            body: messageText,
            ...(fromNumber ? { from_number: fromNumber } : {}),
          });
        }
      } else {
        const formData = new FormData();
        formData.append('recipients', JSON.stringify(selectedContacts.map(c => c.phone)));
        formData.append('message', messageText);
        // The server refuses a file for several recipients of a linked line; do not send one it would reject.
        if (attachments[0] && !isLinkedNumber(fromNumber)) formData.append('files', attachments[0]);
        if (fromNumber) formData.append('from_number', fromNumber);
        await broadcastMutation(formData);
      }
      toast.success("Message sent successfully");
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Failed to send message");
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white border-b px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Send WhatsApp Message</h1>
            <p className="text-sm text-gray-500 mt-1">Send messages to individual or multiple contacts</p>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto p-6">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="p-6 border-b border-gray-200">
            <label className="block text-sm font-medium text-gray-700 mb-3">Send From</label>
            <select
              value={fromNumber}
              onChange={(e) => {
                setFromNumber(e.target.value);
              }}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">Default: {senderLabel()?.text || 'server default'}</option>
              {numbersData?.data?.map(number => (
                <option key={number.phone_number_id} value={number.alias || number.phone_number_id}>
                  {senderLabel(number.phone_number_id)?.text}
                </option>
              ))}
            </select>
          </div>

          <div className="p-6 border-b border-gray-200">
            <label className="block text-sm font-medium text-gray-700 mb-3">Message Type</label>
            <div className="flex gap-4">
              <button
                onClick={() => handleMessageTypeChange('single')}
                className={`flex-1 p-4 border-2 rounded-lg transition-all ${messageType === 'single'
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-gray-200 hover:border-gray-300'
                  }`}
              >
                <User className={`w-6 h-6 mx-auto mb-2 ${messageType === 'single' ? 'text-blue-500' : 'text-gray-400'
                  }`} />
                <p className={`text-sm font-medium ${messageType === 'single' ? 'text-blue-700' : 'text-gray-700'
                  }`}>Single Contact</p>
              </button>
              <button
                onClick={() => handleMessageTypeChange('multiple')}
                className={`flex-1 p-4 border-2 rounded-lg transition-all ${messageType === 'multiple'
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-gray-200 hover:border-gray-300'
                  }`}
              >
                <Users className={`w-6 h-6 mx-auto mb-2 ${messageType === 'multiple' ? 'text-blue-500' : 'text-gray-400'
                  }`} />
                <p className={`text-sm font-medium ${messageType === 'multiple' ? 'text-blue-700' : 'text-gray-700'
                  }`}>Multiple Contacts</p>
              </button>
            </div>
          </div>

          <div className="p-6 border-b border-gray-200">
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Recipients {messageType === 'multiple' && `(${selectedContacts.length} selected)`}
            </label>

            <div className="relative">
              <div className="flex items-center gap-2 flex-wrap p-3 border border-gray-300 rounded-lg min-h-[48px]">
                {selectedContacts.map(contact => (
                  <div
                    key={contact.id}
                    className="flex items-center gap-2 px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm"
                  >
                    <span>{contact.name}</span>
                    <button
                      onClick={() => handleRemoveContact(contact.id)}
                      className="hover:bg-blue-200 rounded-full p-0.5"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
                {messageType === 'multiple' ? (
                  <button
                    onClick={() => setShowContactList(!showContactList)}
                    className="flex items-center gap-2 px-3 py-1 text-sm text-gray-600 hover:text-gray-900"
                  >
                    <Plus className="w-4 h-4" />
                    Add contact
                  </button>
                ) :
                  messageType === 'single' && selectedContacts.length === 0 && (
                    <button
                      onClick={() => setShowContactList(!showContactList)}
                      className="flex items-center gap-2 px-3 py-1 text-sm text-gray-600 hover:text-gray-900"
                    >
                      <Plus className="w-4 h-4" />
                      Add contact
                    </button>
                  )}
              </div>

              {showContactList && (
                <div className="absolute z-10 mt-2 w-full bg-white border border-gray-300 rounded-lg shadow-lg max-h-64 overflow-hidden">
                  <div className="p-3 border-b">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                      <input
                        type="text"
                        placeholder="Search contacts..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                  </div>
                  <div className="overflow-y-auto max-h-48">
                    {filteredContacts.map(contact => (
                      <button
                        key={contact.id}
                        onClick={() => handleContactSelect(contact)}
                        className="w-full p-3 hover:bg-gray-50 text-left flex items-center gap-3 border-b last:border-b-0"
                      >
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-semibold">
                          {contact.name.charAt(0)}
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">{contact.name}</p>
                          <p className="text-sm text-gray-500">{contact.phone}</p>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="p-6 border-b border-gray-200">
            <label className="block text-sm font-medium text-gray-700 mb-3">Message</label>
            <textarea
              value={messageText}
              onChange={(e) => setMessageText(e.target.value)}
              placeholder="Type your message here..."
              rows={6}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
            />
            <div className="flex items-center justify-between mt-3">
              <div className="flex items-center gap-2">
                <button className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
                  <Smile className="w-5 h-5 text-gray-600" />
                </button>
              </div>
              <p className="text-sm text-gray-500">{messageText.length} characters</p>
            </div>
          </div>

          {isLinkedNumber(fromNumber) && messageType === 'multiple' ? (
            <div className="p-6 border-b border-gray-200">
              <label className="block text-sm font-medium text-gray-700 mb-3">Attachments</label>
              <p className="text-sm text-gray-500">
                Files cannot be sent to several people at once from a linked line. Send the text here,
                then send the file to each person individually.
              </p>
            </div>
          ) : (
          <div className="p-6 border-b border-gray-200">
            <label className="block text-sm font-medium text-gray-700 mb-3">Attachments</label>

            <div className="grid grid-cols-4 gap-3 mb-4">
              <label className="flex flex-col items-center justify-center p-4 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-blue-500 hover:bg-blue-50 transition-colors">
                <input
                  type="file"
                  multiple
                  onChange={handleFileUpload}
                  className="hidden"
                  accept="image/*,video/*,.pdf,.doc,.docx"
                />
                <ImageIcon className="w-6 h-6 text-gray-400 mb-2" />
                <span className="text-xs text-gray-600">Image</span>
              </label>

              <label className="flex flex-col items-center justify-center p-4 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-blue-500 hover:bg-blue-50 transition-colors">
                <input
                  type="file"
                  multiple
                  onChange={handleFileUpload}
                  className="hidden"
                  accept="video/*"
                />
                <Video className="w-6 h-6 text-gray-400 mb-2" />
                <span className="text-xs text-gray-600">Video</span>
              </label>

              <label className="flex flex-col items-center justify-center p-4 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-blue-500 hover:bg-blue-50 transition-colors">
                <input
                  type="file"
                  multiple
                  onChange={handleFileUpload}
                  className="hidden"
                  accept=".pdf,.doc,.docx"
                />
                <FileText className="w-6 h-6 text-gray-400 mb-2" />
                <span className="text-xs text-gray-600">Document</span>
              </label>

              <label className="flex flex-col items-center justify-center p-4 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-blue-500 hover:bg-blue-50 transition-colors">
                <input
                  type="file"
                  multiple
                  onChange={handleFileUpload}
                  className="hidden"
                />
                <Upload className="w-6 h-6 text-gray-400 mb-2" />
                <span className="text-xs text-gray-600">Other</span>
              </label>
            </div>

            {attachments.length > 0 && (
              <div className="space-y-2">
                {attachments.map((file, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200"
                  >
                    <div className="flex items-center gap-3">
                      <FileText className="w-5 h-5 text-gray-400" />
                      <div>
                        <p className="text-sm font-medium text-gray-900">{file.name}</p>
                        <p className="text-xs text-gray-500">{(file.size / 1024).toFixed(2)} KB</p>
                      </div>
                    </div>
                    <button
                      onClick={() => handleRemoveAttachment(index)}
                      className="p-1 hover:bg-gray-200 rounded transition-colors"
                    >
                      <X className="w-4 h-4 text-gray-600" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
          )}

          <div className="p-6 flex items-center justify-end">
            <button
              onClick={handleSendMessage}
              disabled={selectedContacts.length === 0 || !messageText.trim() || broadcastIsPending || sendWithAttachmentsIsPending || sendIsPending}
              className="px-6 py-2 text-sm font-medium text-white bg-green-500 rounded-lg hover:bg-green-600 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center gap-2"
            >
              <Send className="w-4 h-4" />
              Send Message
            </button>
          </div>
        </div>
      </div>

      <Toaster position='top-right' richColors={true} duration={5000} />
    </div>
  );
};

export default SendMessagePage;
